import { Card } from 'antd';
import { ItemCardMessage, ItemCardMessageData } from 'components/Dialogue';
import BubbleMessage from 'components/Dialogue/components/Message/BubbleMessage';
import { LocalMessage } from 'hooks/business/conversation';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { itemCardProduct, ItemCardProductParams } from 'services/api/conversation';
import chatMsgRenderFailReport from 'components/Dialogue/components/msgReport';
import { cloneDeep } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import { useChatConversationContext } from '../../contexts/ChatConversationContext';
import { useQuery, QueryStatus } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';
import { useInViewport } from 'ahooks';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import { formatNumber } from 'utils/numberFormat';

export interface LazyItemMessageProps {
  storeId: Conversation.Conversation['storeId'];
  externalItemId: Conversation.MsgItem['externalItemId'];
  externalStoreId: string;
  rawMsg: LocalMessage;
  children?: (component: React.ReactNode, status: QueryStatus) => React.ReactNode;
  quote?: React.ReactNode;
  isUserEnter?: boolean;
  lazyScrollContainer?: Element | null;
  className?: HTMLDivElement['className'];
}

const LazyItemMessage = (props: LazyItemMessageProps) => {
  const { t } = useTranslate();
  const [content, setContent] = useState<ItemCardMessageData>();
  const {
    externalItemId,
    externalStoreId,
    storeId,
    children,
    rawMsg,
    quote,
    isUserEnter,
    lazyScrollContainer,
    className,
  } = props;

  const { currentConversation: conversation } = useChatConversationContext();

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const ref = useRef<HTMLDivElement>(null);
  const [inViewPort = false] = useInViewport(ref, {
    root: lazyScrollContainer,
  });

  const queryParams = useMemo(
    () => ({
      storeId,
      externalItemId,
      needStockRule: true,
    }),
    [storeId, externalItemId],
  );
  const {
    status,
    data: product,
    isError,
  } = useQuery({
    enabled: !!conversation && (inViewPort || isFaq),
    queryKey: [ApiQueryKey.ItemCardProduct, queryParams],
    queryFn: ({ queryKey, signal }) =>
      itemCardProduct(queryKey[1] as ItemCardProductParams, signal),
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    if (product) {
      const { channel } = conversation;
      const {
        itemName: title,
        itemUrl,
        imageUrl: [img],
        unit,
        skuLowPrice,
        skuHighPrice,
        originalHighPrice,
        isPreOrder,
        itemSku,
        spuStockStatus,
      } = product;

      const priceNumber =
        channel === 'LAZADA' && skuHighPrice === 0 ? originalHighPrice : skuHighPrice;

      const price =
        skuLowPrice === skuHighPrice
          ? `${unit} ${formatNumber(priceNumber, '0,0.00')}`
          : `${unit} ${formatNumber(skuLowPrice, '0,0.00')} - ${unit} ${formatNumber(
              skuHighPrice,
              '0,0.00',
            )}`;

      const originPrice =
        originalHighPrice > priceNumber
          ? `${unit} ${formatNumber(originalHighPrice, '0,0.00')}`
          : null;

      const discount =
        originalHighPrice > priceNumber
          ? `${formatNumber((priceNumber - originalHighPrice) / originalHighPrice, '0.00%')}`
          : null;

      setContent({
        id: externalItemId,
        title,
        price,
        link: itemUrl,
        img,
        originPrice,
        discount,
        isPreOrder,
        itemSku,
        externalStoreId,
        spuStockStatus,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    if (isError) {
      chatMsgRenderFailReport('item', cloneDeep(rawMsg));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const component: React.ReactNode = useMemo(() => {
    if (status === 'loading') {
      return <Card loading />;
    }
    if (status === 'success' && content) {
      return (
        <ItemCardMessage
          dataSource={content}
          quote={quote}
          isUserEnter={isUserEnter}
          lazyScrollContainer={lazyScrollContainer}
        />
      );
    }
    return (
      <BubbleMessage>
        {t('scene.chatWorkspace.messageThread.lazyCard.error', { type: 'Product' })}
      </BubbleMessage>
    );
  }, [content, status, quote, isUserEnter, lazyScrollContainer, t]);

  return (
    <div className={className} ref={ref}>
      {typeof children === 'function' ? children(component, status) : component}
    </div>
  );
};
export default LazyItemMessage;
